import { pick } from 'lodash';
import { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Me, Person } from 'types';
import { useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { useMe } from 'utils/util/hooks';
import ValidatedFormWithSteps from 'views/Widgets/Forms/ValidatedFormWithSteps';
import { useWizardConfiguration } from './configuration';
import { useAgentMetadata } from './useAgentMetadata';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onSuccess: (createdAgent: unknown) => void;
}
interface FormObject {
  id: number;
  initial_template: string;
  name: string;
  description: string;
  visibility: 'I' | 'P';
  owners: Person[];
  viewers?: unknown;
  instructions?: string;
  documents?: any[];
  associated_documents?: any[];
  associated_skills?: any[];
  tone_preset?: string;
  challenge_level?: number;
  empathy_balance?: number;
  directness?: number;
  formality?: number;
  proactivity?: number;
  config_purpose?: string;
  config_responsibilities?: string;
  config_guidelines?: string;
  config_custom_instructions?: string;
}

const CreateAgentModal: FC<Props> = ({ isOpen, toggle, onSuccess }) => {
  const { formatMessage } = useIntl();
  const me: Me | undefined = useMe();
  const [agent, setAgent] = useState<FormObject>({
    id: 0,
    name: 'New Agent',
    initial_template: 'MANAGER_COACH',
    description: '',
    visibility: 'I',
    owners: me ? [me] : [],
    documents: [],
    associated_skills: [],
    tone_preset: '',
    config_purpose: '',
    config_responsibilities: '',
    config_guidelines: '',
    config_custom_instructions: '',
  });
  const { templates } = useAgentMetadata();

  const handleCallback = useCallback(
    (data: unknown, error: unknown) => {
      if (data) {
        toast.success(
          formatMessage({
            id: 'app.views.ai.agents.agents_admin.create_agents_modal.create_modal.success',
            defaultMessage: 'Agent created successfully',
          })
        );
        onSuccess(data);
        toggle();
      } else if (error) {
        toast.error(
          formatMessage({
            id: 'app.views.ai.agents.agents_admin.create_agents_modal.create_modal.error',
            defaultMessage: 'Failed to create agent',
          })
        );
      }
    },
    [formatMessage, onSuccess, toggle]
  );

  const handleStepCallback = useCallback(
    (data: unknown, error: unknown) => {
      if (data) {
        const template = templates.find(
          // @ts-expect-error
          (t) => t.id === data.initial_template
        )!;
        setAgent({
          ...data,
          // @ts-expect-error
          ...data.behavior,
          // @ts-expect-error
          associated_skills: (data?.associated_skills ?? []).map(
            (id: string) =>
              template.skills.find((c) => c.id === id) as unknown as any
          ),
        } as FormObject);
      }
      if (error) {
        console.error('Error in step:', error);
      }
    },
    [templates]
  );

  const transformObjectBeforeSubmit = useCallback(
    (obj: Record<string, unknown>) => {
      return {
        ...obj,
        owners: (obj.owners as Array<{ id: string }>).map((o) => o.id),
        associated_documents: (
          (obj.associated_documents ?? []) as Array<{ id: string }>
        ).map((d) => d.id),
        associated_skills: (
          (obj.associated_skills ?? []) as Array<{ id: string }>
        ).map((c) => c.id),
        custom_skills: ((obj.custom_skills ?? []) as Array<{ id: string }>).map(
          (c) => c.id
        ),
        behavior: pick(
          obj,
          'tone_preset',
          'challenge_level',
          'empathy_balance',
          'directness',
          'formality',
          'proactivity'
        ),
        config: {
          purpose: obj.config_purpose || '',
          responsibilities: obj.config_responsibilities || '',
          guidelines: obj.config_guidelines || '',
          custom_instructions: obj.config_custom_instructions || '',
        },
      };
    },
    []
  );

  const handleOnValidate = useCallback(
    (obj) => {
      if (obj.visibility === 'C' && !obj.viewers) {
        return {
          viewers: formatMessage({
            id: 'app.views.ai.agents.agents_admin.create_agents_modal.create_modal.validation.viewers_required',
            defaultMessage:
              'Please specify a filter when visibility is set to custom.',
          }),
        };
      }
      return {};
    },
    [formatMessage]
  );

  const proxyParameters = useProxyParameters();

  const handleOnSubmit = useCallback(
    (submittedObject, onSubmit) => {
      ConfirmAPI.sendRequestToConfirm(
        agent?.id ? 'PATCH' : 'POST',
        agent?.id ? `/agents/${agent.id}` : '/agents',
        { ...proxyParameters, ...submittedObject },
        (data, error: unknown, hardErrorMessage: unknown) => {
          onSubmit(data, error, hardErrorMessage);
        }
      );
    },
    [proxyParameters, agent?.id]
  );

  const { steps } = useWizardConfiguration({
    toggle,
    template_id: agent.initial_template,
    templates,
  });

  return (
    <>
      {isOpen && (
        <ValidatedFormWithSteps
          object={agent}
          submitText={formatMessage({
            id: 'app.views.ai.agents.agents_admin.create_agents_modal.create_modal.submit_text',
            defaultMessage: 'Create',
          })}
          title={formatMessage({
            id: 'app.views.ai.agents.agents_admin.create_agents_modal.create_modal.title',
            defaultMessage: 'Create New Agent',
          })}
          callback={handleCallback}
          stepCallback={handleStepCallback}
          onValidate={handleOnValidate}
          onCancel={toggle}
          transformObjectBeforeSubmit={transformObjectBeforeSubmit}
          // @ts-expect-error
          submitPromise={handleOnSubmit}
          // @ts-expect-error
          steps={steps}
        />
      )}
    </>
  );
};

export default CreateAgentModal;
